import React, { useCallback, useContext, useEffect, useRef } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { OmniboxSearchResult } from "common-client/generated/graphql";
import { useDebouncedCallback } from "common-client/utils/useDebouncedCallback";
import { useExecuteSearchLazyQuery } from "../../generated/graphql";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import { FlexColumn } from "../Common/__styles__/Layout";
import Divider from "../Common/Divider";
import { Icon } from "../Common/Icons/LucideIcons";
import { Text } from "../Inputs";
import { InternalMapContext } from "../Maps/InternalMapContextProvider";
import { SearchResultProps } from ".";
import {
  BackgroundPanelOverlay,
  LeftContentWrapper,
  OmniboxPanelContainer,
  OmniboxSearchContainer,
  OmniboxSearchInputWrapper,
} from "./__styles__/OmniboxSearch";
import { OmniboxRecentSearches } from "./OmniboxRecentSearches";
import { OmniboxResults } from "./OmniboxResults";
import { SearchContext } from "./SearchContext";
import { searchResultCallbackProps } from "./utils";

const OmniboxSearchInputField = ({
  outline,
  value,
  onChange,
  accountId,
}: {
  outline: boolean;
  value: string;
  onChange?: (value: string) => void;
  accountId?: string;
}) => {
  const { searchQueryState, searchQueryStateDispatch, searchResultsDispatch } =
    useContext(SearchContext);

  const [executeSearch] = useExecuteSearchLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: ({ search }) => {
      searchResultsDispatch({ type: "searchComplete", results: search });
    },
  });

  const debouncedExecuteSearch = useDebouncedCallback(async (val: string) => {
    searchResultsDispatch({ type: "searchStarted", query: val });
    await executeSearch({ variables: { query: val, accountId } });
  }, 250);

  const handleChange = async (inputValue: string) => {
    onChange?.(inputValue);
    if (inputValue) {
      searchQueryStateDispatch({ type: "searchEntered", query: inputValue });
      await debouncedExecuteSearch(inputValue);
    } else {
      searchQueryStateDispatch({ type: "searchCleared" });
      searchResultsDispatch({ type: "resetSearch" });
    }
  };

  const handleClear = () => {
    onChange?.("");
    searchQueryStateDispatch({ type: "resetSearch" });
    searchResultsDispatch({ type: "resetSearch" });
  };

  return (
    <OmniboxSearchInputWrapper outline={outline}>
      <LeftContentWrapper>
        <Icon iconName="search" color="contentSecondary" size="16" />
        <Text
          name="omnibox-search-input"
          data-testid="omnibox-search-input"
          placeholder="Search..."
          onChange={handleChange}
          onFocus={() =>
            searchQueryStateDispatch({
              type: "setSearchPanel",
              searchPanelOpen: true,
            })
          }
          size="small"
          value={value}
          maxLength={256}
          style={{
            padding: 0,
            border: "none",
            height: "24px",
          }}
        />
      </LeftContentWrapper>
      {(value || searchQueryState.searchPanelOpen) && (
        <Icon
          iconName="x"
          color="contentSecondary"
          size="16"
          data-testid="omnibox-search-clear"
          style={{ cursor: "pointer" }}
          onClick={handleClear}
        />
      )}
    </OmniboxSearchInputWrapper>
  );
};

const OmniboxSearchInput = <T extends FieldValues>({
  outline,
  accountId,
  ...formControlProps
}: {
  accountId?: string;
  outline: boolean;
} & FormControlProps<T>) => {
  const { searchQueryState } = useContext(SearchContext);

  if (formControlProps.control) {
    return (
      <Controller
        {...formControlProps}
        render={({ field }) => (
          <OmniboxSearchInputField
            outline={outline}
            value={field.value ?? ""}
            onChange={field.onChange}
            accountId={accountId}
          />
        )}
      />
    );
  }

  return (
    <OmniboxSearchInputField
      outline={outline}
      value={searchQueryState.query ?? ""}
      accountId={accountId}
    />
  );
};

export type FormControlProps<T extends FieldValues> =
  | {
      control: Control<T>;
      name: Path<T>;
      rules?: RegisterOptions<T, Path<T>>;
    }
  | {
      control?: never;
      name?: never;
      rules?: never;
    };

type OmniboxSearchProps<T extends FieldValues> = {
  accountId?: string;
  handleSearchResultClick: (searchResult: SearchResultProps) => void;
  backgroundPanel?: boolean;
  enableRecentSearches?: boolean;
  compactSearchPanel?: boolean;
  outline?: boolean;
  overlaySearchPanel?: boolean;
} & FormControlProps<T>;

export const OmniboxSearch = <T extends FieldValues>({
  accountId,
  handleSearchResultClick,
  backgroundPanel = false,
  enableRecentSearches = false,
  compactSearchPanel = false,
  outline = false,
  overlaySearchPanel = false,
  ...formControlProps
}: OmniboxSearchProps<T>) => {
  const {
    searchQueryState,
    searchQueryStateDispatch,
    searchResultsDispatch,
    addRecentSearch,
    setHandleSearchResultClick,
  } = useContext(SearchContext);
  // needed while we still have the old search around
  const { setResetSearchCategory } = useContext(InternalMapContext);
  useEffect(() => {
    if (setResetSearchCategory) {
      setResetSearchCategory(() => {
        searchQueryStateDispatch({ type: "resetSearch" });
        searchResultsDispatch({ type: "resetSearch" });
      });
    }
  }, [setResetSearchCategory, searchQueryStateDispatch, searchResultsDispatch]);

  const searchFieldDirty =
    formControlProps.control ?
      formControlProps.control.getFieldState(formControlProps.name).isDirty
    : true;

  const wrappedHandleSearchResultClick = useCallback(
    (result: OmniboxSearchResult) => {
      searchQueryStateDispatch({ type: "resetSearch" });
      searchResultsDispatch({ type: "resetSearch" });
      if (enableRecentSearches) {
        addRecentSearch(searchQueryState.query ?? "", result);
      }
      handleSearchResultClick(searchResultCallbackProps(result));
    },
    [
      handleSearchResultClick,
      searchQueryStateDispatch,
      searchResultsDispatch,
      addRecentSearch,
      enableRecentSearches,
      searchQueryState.query,
    ],
  );

  useEffect(() => {
    setHandleSearchResultClick(wrappedHandleSearchResultClick);
  }, [wrappedHandleSearchResultClick, setHandleSearchResultClick]);

  const backgroundPanelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter({
    ref: [searchRef, backgroundPanelRef],
    onOutsideInteraction: () => {
      if (searchQueryState.searchPanelOpen) {
        searchQueryStateDispatch({
          type: "setSearchPanel",
          searchPanelOpen: false,
        });
      }
    },
    dependencies: [searchQueryState.searchPanelOpen],
  });

  return (
    <>
      {backgroundPanel && searchQueryState.searchPanelOpen && (
        <BackgroundPanelOverlay ref={backgroundPanelRef} />
      )}
      <OmniboxSearchContainer ref={searchRef}>
        <FlexColumn>
          <OmniboxSearchInput
            outline={outline}
            accountId={accountId}
            {...formControlProps}
          />
          {backgroundPanel && searchQueryState.searchPanelOpen && (
            <Divider extension={16} />
          )}
        </FlexColumn>
        {searchQueryState.searchPanelOpen &&
          searchQueryState.query &&
          searchFieldDirty && (
            <OmniboxPanelContainer
              outline={outline}
              compact={compactSearchPanel}
              overlay={overlaySearchPanel}
            >
              <OmniboxResults
                handleSearchResultClick={wrappedHandleSearchResultClick}
                expandPlaceholders={backgroundPanel}
              />
            </OmniboxPanelContainer>
          )}
        {searchQueryState.searchPanelOpen &&
          !searchQueryState.query &&
          enableRecentSearches && (
            <OmniboxPanelContainer
              outline={outline}
              compact={compactSearchPanel}
              overlay={overlaySearchPanel}
            >
              <OmniboxRecentSearches
                handleSearchResultClick={wrappedHandleSearchResultClick}
                expandPlaceholders={backgroundPanel}
              />
            </OmniboxPanelContainer>
          )}
      </OmniboxSearchContainer>
    </>
  );
};
